import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, XCircle, Wrench } from 'lucide-react';
import { getActiveMetaPages } from '@/services/api/connections';
import VariablesCopyDialog from '@/pages/Meta/Broadcast/components/VariablesCopyDialog';
import type { FunnelValidationResponse } from '@/types/funnel';
import type { Page, PageVariablesValidation } from '@/types/broadcast';

interface FunnelValidationDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    result: FunnelValidationResponse | null;
    onRevalidate: () => void;
}

interface MissingGroup {
    missing: string[];
    pages: PageVariablesValidation[];
}

export default function FunnelValidationDialog({
    open,
    onOpenChange,
    result,
    onRevalidate,
}: FunnelValidationDialogProps) {
    const [copyDialogOpen, setCopyDialogOpen] = useState(false);

    const { data: metaPages = [] } = useQuery({
        queryKey: ['active-meta-pages'],
        queryFn: getActiveMetaPages,
        staleTime: 1000 * 60 * 5,
        enabled: open,
    });

    const pagesWithMissing = result?.pages_with_missing ?? [];

    // Erros de variáveis já aparecem estruturados abaixo; aqui só os demais
    const generalErrors = (result?.errors ?? []).filter(
        (e) => pagesWithMissing.length === 0 || !e.includes('variáveis faltando')
    );

    // Agrupa páginas com o mesmo conjunto de variáveis faltando
    const groups = useMemo<MissingGroup[]>(() => {
        const map = new Map<string, MissingGroup>();
        for (const p of pagesWithMissing) {
            const key = p.variables_missing.join(',');
            const group = map.get(key);
            if (group) {
                group.pages.push(p);
            } else {
                map.set(key, { missing: p.variables_missing, pages: [p] });
            }
        }
        return [...map.values()].sort((a, b) => b.pages.length - a.pages.length);
    }, [pagesWithMissing]);

    if (!result) return null;

    return (
        <>
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <AlertTriangle className="h-5 w-5 text-amber-500" />
                            Funil inválido
                        </DialogTitle>
                    </DialogHeader>

                    <div className="flex-1 overflow-y-auto space-y-5 py-2 pr-1">
                        {generalErrors.length > 0 && (
                            <div>
                                <p className="text-sm font-medium text-gray-700 mb-2">
                                    Problemas de configuração
                                </p>
                                <div className="space-y-1">
                                    {generalErrors.map((err, i) => (
                                        <div
                                            key={i}
                                            className="flex items-start gap-2 rounded-md bg-red-50 px-3 py-2 text-sm text-gray-800"
                                        >
                                            <XCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                                            <span>{err}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {pagesWithMissing.length > 0 && (
                            <div>
                                <p className="text-sm font-medium text-gray-700 mb-2">
                                    {pagesWithMissing.length === 1
                                        ? '1 página com variáveis faltando'
                                        : `${pagesWithMissing.length} páginas com variáveis faltando`}
                                </p>
                                <div className="space-y-3">
                                    {groups.map((group, i) => (
                                        <div key={i} className="rounded-lg border border-red-200 bg-red-50/50 p-3">
                                            <div className="flex items-center gap-1.5 flex-wrap mb-2">
                                                <span className="text-xs text-gray-500 mr-1">Faltando:</span>
                                                {group.missing.map((v) => (
                                                    <Badge key={v} variant="destructive" className="text-xs font-mono">
                                                        {v}
                                                    </Badge>
                                                ))}
                                            </div>
                                            <div className="flex gap-1.5 flex-wrap">
                                                {group.pages.map((p) => (
                                                    <span
                                                        key={p.id}
                                                        className="rounded-md bg-white border border-gray-200 px-2 py-0.5 text-xs text-gray-700"
                                                    >
                                                        {p.page_name}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => onOpenChange(false)}>
                            Fechar
                        </Button>
                        {pagesWithMissing.length > 0 && (
                            <Button
                                onClick={() => setCopyDialogOpen(true)}
                                className="bg-blue-600 hover:bg-blue-700 text-white"
                            >
                                <Wrench className="h-4 w-4 mr-1" />
                                Corrigir variáveis
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <VariablesCopyDialog
                open={copyDialogOpen}
                onOpenChange={setCopyDialogOpen}
                pages={pagesWithMissing}
                selectedPages={(metaPages as any[]).map((p) => ({
                    id: p.id,
                    page_id: p.page_id,
                    page_name: p.page_name ?? p.page_id,
                } as Page))}
                onFixed={() => {
                    setCopyDialogOpen(false);
                    onRevalidate();
                }}
            />
        </>
    );
}
